import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';
import type FacilityView from '@UI/Store/Facilities/Views/FacilityView';

type FacilityFormArgs = {
	name: string;
	address: string | null;
	phoneNumber: string | null;
};

/**
 * `address` and `phoneNumber` carry no format constraint on purpose: international numbers, country
 * codes, extensions and separators vary too widely for a pattern to stay correct.
 */
@ValidatedClass({ ignoreNullValue: true })
class FacilityFormData extends AbstractValidatedClass<FacilityFormArgs> {
	@constraint(Joi.string().max(180), () => 'Le nom de l’agence ne doit pas dépasser 180 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom de l’agence est obligatoire')
	public readonly name: string;

	@constraint(Joi.string().max(255).allow(''), () => 'L’adresse ne doit pas dépasser 255 caractères.')
	public readonly address: string | null;

	@constraint(
		Joi.string().max(30).allow(''),
		() => 'Le numéro de téléphone ne doit pas dépasser 30 caractères.',
	)
	public readonly phoneNumber: string | null;

	constructor(data: FacilityFormArgs) {
		super(data);
		this.name = data.name;
		this.address = data.address;
		this.phoneNumber = data.phoneNumber;
		Object.freeze(this);
	}

	static createEmpty(): FacilityFormData {
		return new FacilityFormData({ name: '', address: null, phoneNumber: null });
	}

	static fromView(facility: FacilityView): FacilityFormData {
		return new FacilityFormData({
			name: facility.name,
			address: facility.address,
			phoneNumber: facility.phoneNumber,
		});
	}

	setName(name: string): this {
		return this.cloneWith({ name } as Partial<FacilityFormArgs>);
	}

	setAddress(address: string): this {
		return this.cloneWith({ address } as Partial<FacilityFormArgs>);
	}

	setPhoneNumber(phoneNumber: string): this {
		return this.cloneWith({ phoneNumber } as Partial<FacilityFormArgs>);
	}
}

export default FacilityFormData;
